import { motion } from "framer-motion";
import { Mail, MapPin, Briefcase, Award, Brain, Cloud, Shield, BarChart3, Zap, Settings } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { TeamMember, Service } from "@/lib/index";

// Helper function to get the appropriate icon component
function getServiceIcon(iconName: string) {
  const iconMap = {
    brain: <Brain className="h-6 w-6" />,
    cloud: <Cloud className="h-6 w-6" />,
    shield: <Shield className="h-6 w-6" />,
    chart: <BarChart3 className="h-6 w-6" />,
    automation: <Zap className="h-6 w-6" />,
    settings: <Settings className="h-6 w-6" />,
  };
  return iconMap[iconName as keyof typeof iconMap] || <Settings className="h-6 w-6" />;
}

interface TeamCardProps {
  member: TeamMember;
}

export function TeamCard({ member }: TeamCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 300, damping: 35 }}
    >
      <Card className="h-full overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10">
        <CardHeader className="space-y-4">
          <div className="flex items-start gap-6">
            {member.image && (
              <div className="flex-shrink-0">
                <div className="relative h-24 w-24 overflow-hidden rounded-full border-2 border-primary/20">
                  <img
                    src={member.image}
                    alt={`${member.name} - ${member.title}`}
                    className="h-full w-full object-cover transition-transform duration-200 hover:scale-105"
                  />
                </div>
              </div>
            )}
            <div className="flex-1 space-y-2">
              <CardTitle className="text-2xl font-bold text-foreground">
                {member.name}
              </CardTitle>
              <CardDescription className="text-base font-medium text-primary">
                {member.title}
              </CardDescription>
              {member.company && (
                <p className="text-sm text-muted-foreground">{member.company}</p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span>{member.location}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Briefcase className="h-4 w-4 text-primary" />
              <span>{member.experience}</span>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-foreground/80">
            {member.description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 300, damping: 35 }}
    >
      <Card className="h-full overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-accent/10">
        <CardHeader className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border/50 bg-background text-primary">
              {getServiceIcon(service.icon)}
            </div>
            <div className="flex-1 space-y-2">
              <CardTitle className="text-xl font-bold text-foreground">
                {service.title}
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {service.description}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-foreground/80">Key Features:</h4>
            <ul className="space-y-2">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Award className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface ContactCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

export function ContactCard({ title, description, icon }: ContactCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
    >
      <Card className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground">
            {icon}
          </div>
          <div className="flex-1 space-y-1">
            <h3 className="font-semibold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
